import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import AddIcon from "@mui/icons-material/Add";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import DeleteIcon from "@mui/icons-material/Delete";
import ArticleIcon from "@mui/icons-material/Article";
import { useTenant } from "../../contexts/TenantContext";
import { useActiveTenant } from "../../hooks/useActiveTenant";
import NoTenantSelected from "../../components/NoTenantSelected";
import {
  fetchAgencies,
  fetchAgencyCois,
  uploadAgencyCoi,
  deleteAgencyCoi,
  fetchAllTenants,
} from "../../api/apiClient";

// ── Status chip helpers ───────────────────────────────────────────────────────

const STATUS_PROPS = {
  active: { label: "Active", color: "success" },
  expiring_soon: { label: "Expiring Soon", color: "warning" },
  expired: { label: "Expired", color: "error" },
};

function CoiStatusChip({ status }) {
  const p = STATUS_PROPS[status] || { label: status, color: "default" };
  return <Chip label={p.label} color={p.color} size="small" />;
}

// ── Upload dialog ─────────────────────────────────────────────────────────────

const EMPTY_FORM = {
  filename: "",
  documentUrl: "",
  validFrom: "",
  validTo: "",
  notes: "",
};

function UploadCoiDialog({ open, onClose, onSave, saving, error }) {
  const [form, setForm] = useState(EMPTY_FORM);

  useEffect(() => {
    if (open) setForm(EMPTY_FORM);
  }, [open]);

  const handle = (field) => (e) =>
    setForm((f) => ({ ...f, [field]: e.target.value }));

  const valid = form.filename.trim() !== "" && form.validFrom !== "";

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Add Certificate of Insurance</DialogTitle>
      <DialogContent
        sx={{ display: "flex", flexDirection: "column", gap: 2, mt: 1 }}
      >
        {error && <Alert severity="error">{error}</Alert>}

        <TextField
          label="Filename / Document Name"
          value={form.filename}
          onChange={handle("filename")}
          required
          fullWidth
        />
        <TextField
          label="Document URL"
          value={form.documentUrl}
          onChange={handle("documentUrl")}
          fullWidth
          helperText="Link to the COI document (upload to your storage first)"
        />
        <TextField
          label="Valid From"
          type="date"
          value={form.validFrom}
          onChange={handle("validFrom")}
          required
          fullWidth
          InputLabelProps={{ shrink: true }}
        />
        <TextField
          label="Valid To (leave blank = no expiry)"
          type="date"
          value={form.validTo}
          onChange={handle("validTo")}
          fullWidth
          InputLabelProps={{ shrink: true }}
        />
        <TextField
          label="Notes"
          value={form.notes}
          onChange={handle("notes")}
          multiline
          rows={2}
          fullWidth
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button
          variant="contained"
          disabled={!valid || saving}
          onClick={() => onSave(form)}
          startIcon={saving ? <CircularProgress size={16} /> : null}
        >
          {saving ? "Saving…" : "Save"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

// ── Main page ─────────────────────────────────────────────────────────────────

export default function AgencyCoiPage() {
  const theme = useTheme();
  const tPrimary = theme.palette.primary.main;
  const { user } = useTenant();
  const { tenantId: activeTenant } = useActiveTenant();
  const navigate = useNavigate();
  const userRoles = Array.isArray(user?.roles) ? user?.roles : [];
  const isGlobalAdmin =
    userRoles.includes("ADMIN") || userRoles.includes("GLOBAL_ADMIN");
  const isAgencyAdmin = userRoles.includes("AGENCY_ADMIN");
  const isCourseCoordinator = userRoles.includes("COURSE_COORDINATOR");
  const [, setTenants] = useState([]);
  const [agencies, setAgencies] = useState([]);
  const [selectedAgencyId, setSelectedAgencyId] = useState(
    user?.agencyId || null,
  );
  const [cois, setCois] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadingAgencies, setLoadingAgencies] = useState(true);
  const [error, setError] = useState(null);

  const [dialogOpen, setDialogOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState(null);

  // For global admins: load all tenants on mount
  useEffect(() => {
    if (!isGlobalAdmin) return;
    fetchAllTenants()
      .then((response) => {
        let tenantList = response;
        if (response?.data && Array.isArray(response.data)) {
          tenantList = response.data;
        } else if (!Array.isArray(response)) {
          tenantList = [];
        }
        setTenants(tenantList);
      })
      .catch((err) => {
        console.error("Failed to load tenants:", err);
        setTenants([]);
      });
  }, [isGlobalAdmin]);

  // Load agencies for active tenant (global admin) or user's tenant (agency admin)
  useEffect(() => {
    setLoadingAgencies(true);
    setError(null);

    // For agency admins, load their agencies
    if (isAgencyAdmin && user?.agencyId) {
      setSelectedAgencyId(user.agencyId);
      setAgencies([
        { id: user.agencyId, name: user.agencyName || "Your Agency" },
      ]);
      setLoadingAgencies(false);
      return;
    }

    if (!activeTenant) {
      setError("Please select a tenant first.");
      setLoadingAgencies(false);
      return;
    }

    // Fetch agencies for the active tenant
    fetchAgencies(activeTenant)
      .then((response) => {
        let list = response;
        if (response?.data && Array.isArray(response.data)) {
          list = response.data;
        } else if (!Array.isArray(response)) {
          list = [];
        }
        setAgencies(list);
        if (list.length > 0 && !selectedAgencyId) {
          setSelectedAgencyId(list[0].id);
        } else if (list.length === 0) {
          setSelectedAgencyId(null);
          setError("No agencies available in this tenant.");
        }
      })
      .catch((err) => {
        setError(`Failed to load agencies: ${err?.message || "Unknown error"}`);
      })
      .finally(() => setLoadingAgencies(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    isGlobalAdmin,
    isAgencyAdmin,
    activeTenant,
    user?.agencyId,
    user?.agencyName,
  ]);

  // Load COIs whenever selected agency changes
  useEffect(() => {
    if (!selectedAgencyId) return;
    if (!activeTenant) return;

    setLoading(true);
    setError(null);
    fetchAgencyCois(activeTenant, selectedAgencyId)
      .then((response) => {
        let data = response;
        if (response?.data && Array.isArray(response.data)) {
          data = response.data;
        } else if (!Array.isArray(response)) {
          data = [];
        }
        setCois(data);
      })
      .catch(() => setError("Failed to load COI records."))
      .finally(() => setLoading(false));
  }, [activeTenant, selectedAgencyId]);

  const handleSave = async (form) => {
    setSaving(true);
    setSaveError(null);
    try {
      const newCoi = await uploadAgencyCoi(
        activeTenant,
        selectedAgencyId,
        form,
      );
      setCois((prev) => [newCoi, ...prev]);
      setDialogOpen(false);
    } catch (err) {
      setSaveError(
        err?.response?.data?.message || "Failed to save COI. Please try again.",
      );
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (coiId) => {
    if (!window.confirm("Remove this COI record?")) return;
    try {
      await deleteAgencyCoi(activeTenant, selectedAgencyId, coiId);
      setCois((prev) => prev.filter((c) => c.id !== coiId));
    } catch {
      setError("Failed to delete COI record.");
    }
  };

  const selectedAgency = agencies.find((a) => a.id === selectedAgencyId);

  return (
    <>
      {/* Course Coordinator View - Redirect to class-level COI reports */}
      {isCourseCoordinator && !isAgencyAdmin && !isGlobalAdmin && (
        <Box sx={{ p: 3 }}>
          <Stack spacing={3}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
              <IconButton
                onClick={() => navigate("/")}
                sx={{ "&:hover": { bgcolor: "#f5f5f5" } }}
              >
                <ArrowBackIcon />
              </IconButton>
              <Typography variant="h5" sx={{ fontWeight: 600, fontFamily: "var(--font-display)" }}>
                Insurance (COI) Compliance
              </Typography>
            </Box>

            <Card>
              <CardContent>
                <Stack spacing={3}>
                  <Box>
                    <Typography variant="h6" sx={{ mb: 1, fontWeight: 600, fontFamily: "var(--font-display)" }}>
                      📋 View Class-Level COI Compliance
                    </Typography>
                    <Typography color="textSecondary" sx={{ mb: 3 }}>
                      As a course coordinator, you can view and manage COI
                      compliance for your training classes through the course
                      hub. Here's how:
                    </Typography>
                    <Stack spacing={2}>
                      <Box>
                        <Typography
                          variant="subtitle2"
                          sx={{ fontWeight: 600, mb: 1, fontFamily: "var(--font-display)" }}
                        >
                          Step 1: Go to Course Management
                        </Typography>
                        <Typography
                          color="textSecondary"
                          component="div"
                          sx={{ ml: 2 }}
                        >
                          Navigate to <strong>"Course Management"</strong> from
                          the main menu
                        </Typography>
                      </Box>
                      <Box>
                        <Typography
                          variant="subtitle2"
                          sx={{ fontWeight: 600, mb: 1, fontFamily: "var(--font-display)" }}
                        >
                          Step 2: Select Your Course
                        </Typography>
                        <Typography
                          color="textSecondary"
                          component="div"
                          sx={{ ml: 2 }}
                        >
                          Click on the course you want to manage
                        </Typography>
                      </Box>
                      <Box>
                        <Typography
                          variant="subtitle2"
                          sx={{ fontWeight: 600, mb: 1, fontFamily: "var(--font-display)" }}
                        >
                          Step 3: Open the Reports Tab
                        </Typography>
                        <Typography
                          color="textSecondary"
                          component="div"
                          sx={{ ml: 2 }}
                        >
                          Once in the course hub, select the{" "}
                          <strong>"Reports"</strong> tab
                        </Typography>
                      </Box>
                      <Box>
                        <Typography
                          variant="subtitle2"
                          sx={{ fontWeight: 600, mb: 1, fontFamily: "var(--font-display)" }}
                        >
                          Step 4: View COI Compliance Report
                        </Typography>
                        <Typography
                          color="textSecondary"
                          component="div"
                          sx={{ ml: 2 }}
                        >
                          Select the <strong>"COI Compliance"</strong> report to
                          see insurance status for all enrolled students
                        </Typography>
                      </Box>
                    </Stack>
                  </Box>

                  <Divider />

                  <Box>
                    <Typography variant="h6" sx={{ mb: 2, fontWeight: 600, fontFamily: "var(--font-display)" }}>
                      ✅ What You'll See
                    </Typography>
                    <Typography color="textSecondary" component="div">
                      The COI compliance report shows:
                    </Typography>
                    <ul style={{ marginTop: "1rem", paddingLeft: "1.5rem" }}>
                      <li>
                        <Typography color="textSecondary">
                          Each student's COI status (Valid, Expired, Missing)
                        </Typography>
                      </li>
                      <li>
                        <Typography color="textSecondary">
                          Certificate validity dates
                        </Typography>
                      </li>
                      <li>
                        <Typography color="textSecondary">
                          Download links to COI documents
                        </Typography>
                      </li>
                    </ul>
                  </Box>
                </Stack>
              </CardContent>
            </Card>

            <Card sx={{ bgcolor: "var(--color-primary-10)" }}>
              <CardContent>
                <Box sx={{ display: "flex", gap: 2 }}>
                  <Typography
                    variant="h4"
                    sx={{ color: tPrimary, minWidth: 32 }}
                  >
                    ℹ️
                  </Typography>
                  <Box>
                    <Typography
                      variant="subtitle2"
                      sx={{ fontWeight: 600, mb: 1, fontFamily: "var(--font-display)" }}
                    >
                      Agency-Level COI Management
                    </Typography>
                    <Typography color="textSecondary">
                      To manage agency COI documents (upload/update certificates
                      of insurance), contact your ADMIN or AGENCY_ADMIN.
                    </Typography>
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Stack>
        </Box>
      )}

      {/* Agency Admin / Global Admin View */}
      {(isAgencyAdmin || isGlobalAdmin) && (
        <>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2 }}>
            <ArticleIcon color="primary" />
            <Typography variant="h5" sx={{ fontFamily: "var(--font-display)" }}>Certificate of Insurance (COI)</Typography>
          </Box>

          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            Manage insurance certificates on file for your agency. A valid COI
            may be required before members can register for classes.
          </Typography>

          {isGlobalAdmin && !activeTenant ? (
            <NoTenantSelected message="Select a training center from the top bar to manage agency COI records." />
          ) : loadingAgencies ? (
            <Box sx={{ textAlign: "center", py: 4 }}>
              <CircularProgress />
              <Typography variant="body2" sx={{ mt: 2 }}>
                Loading agencies...
              </Typography>
            </Box>
          ) : error && !agencies.length ? (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          ) : agencies.length === 0 ? (
            <Alert severity="info">
              No agencies available. Contact your administrator.
            </Alert>
          ) : (
            <>
              {/* Agency selector (global admin with multiple agencies) */}
              {isGlobalAdmin && agencies.length > 1 && (
                <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap", mb: 2 }}>
                  {agencies.map((a) => (
                    <Button
                      key={a.id}
                      variant={
                        a.id === selectedAgencyId ? "contained" : "outlined"
                      }
                      size="small"
                      onClick={() => setSelectedAgencyId(a.id)}
                    >
                      {a.name}
                    </Button>
                  ))}
                </Box>
              )}

              {error && (
                <Alert severity="error" sx={{ mb: 2 }}>
                  {error}
                </Alert>
              )}

              {selectedAgency && (
                <Paper sx={{ p: 2 }} elevation={1}>
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      mb: 2,
                    }}
                  >
                    <Typography variant="h6">{selectedAgency.name}</Typography>
                    <Button
                      variant="contained"
                      startIcon={<AddIcon />}
                      onClick={() => setDialogOpen(true)}
                      disabled={!selectedAgency || !activeTenant}
                    >
                      Add COI
                    </Button>
                  </Box>

                  {loading ? (
                    <Box sx={{ textAlign: "center", py: 4 }}>
                      <CircularProgress />
                    </Box>
                  ) : cois.length === 0 ? (
                    <Alert severity="info">
                      No COI records on file for this agency. Add one above.
                    </Alert>
                  ) : (
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell>Document</TableCell>
                          <TableCell>Valid From</TableCell>
                          <TableCell>Valid To</TableCell>
                          <TableCell>Status</TableCell>
                          <TableCell>Uploaded</TableCell>
                          <TableCell>Notes</TableCell>
                          <TableCell align="right">Actions</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {cois.map((coi) => (
                          <TableRow key={coi.id} hover>
                            <TableCell>
                              {coi.documentUrl ? (
                                <a
                                  href={coi.documentUrl}
                                  target="_blank"
                                  rel="noreferrer"
                                >
                                  {coi.filename}
                                </a>
                              ) : (
                                coi.filename
                              )}
                            </TableCell>
                            <TableCell>{coi.validFrom ?? "—"}</TableCell>
                            <TableCell>{coi.validTo ?? "No expiry"}</TableCell>
                            <TableCell>
                              <CoiStatusChip status={coi.status} />
                            </TableCell>
                            <TableCell>
                              {coi.uploadedAt
                                ? new Date(coi.uploadedAt).toLocaleDateString()
                                : "—"}
                            </TableCell>
                            <TableCell
                              sx={{
                                maxWidth: 180,
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                                whiteSpace: "nowrap",
                              }}
                            >
                              <Tooltip title={coi.notes || ""}>
                                <span>{coi.notes || "—"}</span>
                              </Tooltip>
                            </TableCell>
                            <TableCell align="right">
                              <Tooltip title="Delete">
                                <IconButton
                                  size="small"
                                  color="error"
                                  onClick={() => handleDelete(coi.id)}
                                >
                                  <DeleteIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  )}
                </Paper>
              )}
            </>
          )}

          <UploadCoiDialog
            open={dialogOpen}
            onClose={() => setDialogOpen(false)}
            onSave={handleSave}
            saving={saving}
            error={saveError}
          />
        </>
      )}
    </>
  );
}
